//#region [IMPORT]

// === THIRD-PARTY ===
import React, { useEffect } from 'react';
import { useParams } from 'react-router';
import dayjs from 'dayjs';

// === STYLE / MATERIAL UI ===
import { useTheme } from '@mui/material/styles';
import {
  Box,
  CircularProgress,
  Paper,
  Typography,
} from '@mui/material';

// === COMPONENTS ===
import { FileInfo   } from './components/FileInfo/FileInfo'     ;
import { Exceptions } from './components/Exceptions/Exceptions' ;
import { DocumentView } from './components/DocumentView/DocumentView';
import { MissingDocs } from './components/MissingDocs/MissingDocs';
import { HeaderFileView } from './components/HeaderFileView/HeaderFileView';

// === HOOKS ===
import { useAnalytics } from '../AnalyticsContext/AnalyticsContext';

// === API ===
import { on, removeListener, sendMessageData } from 'api/prokey/prokey.websocket';

// === UTILS ===
import { getHexAlpha } from 'components/styles.utils';

//#endregion

//-----------------------------------------------------------------
export function FileView() {
  //-----------------------------------------------------------------

  //#region [VALUES]

  const { file_id } = useParams();

  const { file_name, rules, is_loading } = useAnalytics();

  //#endregion

  //#region [STYLES]

  const theme = useTheme();
  const _styles = {
    container: {
      height: '100%',
      boxShadow: 0,
      padding   : '12px',
    },
    card: {
      width        : '100%',
      maxWidth     : '100%',
      overflowY    : 'hidden',
    },
    controls: {
      display      : 'flex',
      borderTop    : '1px solid ' + theme.palette.background.primary[theme.palette.mode],
      borderRadius : '1rem 1rem 0 0',
      backgroundColor: theme.palette.background.primary[theme.palette.mode],
    },
    title: {
      display      : 'flex',
      flexGrow     : 1,
      borderTop    : '1px solid ' + theme.palette.background.primary[theme.palette.mode],
      borderRadius : '1rem',
      backgroundColor: theme.palette.background.primary[theme.palette.mode],
    },
    container_content: {
      display : 'block',
      height  : '100%' ,
    },
    container_file_info: {
      width        :'100%'  ,
      height       :'5rem'  ,
      display      :'block' ,
    },
    container_rule_info: {
      width        :'100%' ,
      height       :'calc( 100% - 3rem )'  ,
      display      :'flex' ,
      backgroundColor : theme.palette.panel.main,
    },
    container_documents: {
      width   :'calc( 40% )' ,
      height  :'calc( 100% - 7rem )' ,
      display :'block'       ,
    },
    task_queue_table: {
      width          : '100%',
      'table-layout' : 'fixed',
    },
    table_header_cell: {
      backgroundColor : theme.palette.primary.main + getHexAlpha(100),
      color           : theme.palette.primary.contrastText,
    },
    task_queue_text: {
      margin   : '1rem',
      fontSize : 'x-large',
    },
    right: {
      marginLeft: 'auto'
    },
    align_right: {
      float: "right",
    },
    height: {
      height: '100%',
    },
    button_export: {
      height: '4rem',
      margin: '0 6px',
    },
    button_evaluate: {
      height: '4rem',
      margin: '0 6px',
    },
    button_close: {
      height: '4rem',
      margin: '0 6px',
    },
    spinner_box: {
      position  : 'fixed' ,
      top       : '50%'   ,
      left      : '50%'   ,
      height    : '14rem' ,
      width     : '10rem' ,
      textAlign : 'center'
    },
  }

  const
    CONTAINER = {
      sx: _styles.container,
    },
    CONTAINER_CONTENT = {
      sx: _styles.container_content,
    },
    CONTAINER_FILE_INFO = {
      sx: _styles.container_file_info,
    },
    CONTAINER_RULE_INFO = {
      sx: _styles.container_rule_info,
    };
    
  //#endregion

  //#region [EFFECT]

  useEffect( () => {
    //-----------------------------------------------------------------
    function listener_status(data) {
      //-----------------------------------------------------------------
      sendMessageData( 'FILE_ANALYTICS_REQUEST', { file_id } );
    }

    on( 'FILE_EVALUATE_STATUS' , listener_status );

    sendMessageData( 'FILE_ANALYTICS_REQUEST', { file_id } );
    
    return () => {
      removeListener( 'FILE_EVALUATE_STATUS' , listener_status );
    };
  }, [] ); // empty dependency = called only once at startup

  useEffect( () => {
    on( 'FILE-RESULTS', downloadResults )
    
    return () => {
      removeListener( 'FILE-RESULTS' , downloadResults );
    };
  }, [file_name] );

  //#endregion

  //#region [MODIFY]

  //-----------------------------------------------------------------
  function downloadResults( data ) {
    //-----------------------------------------------------------------
    if( !data ) {
      return;
    }

    const date_curr = dayjs().format( 'YYYYMMDD' );
    const download_file_name = `file-analysis-${file_name}-${date_curr}.json`;

    const json_string    = JSON.stringify( data );
    const json_blob      = new Blob( [json_string], { type: 'application/json' } );
    const json_url       = window.URL.createObjectURL( json_blob );
    const json_hyperlink = document.createElement('a');

    json_hyperlink.setAttribute('href', json_url);
    json_hyperlink.setAttribute('download', download_file_name);
    json_hyperlink.click();
    window.URL.revokeObjectURL( json_url );
  };

  //#endregion

  //#region [EVENTS]

  //#endregion

  //#region [DISPLAY]

  //-----------------------------------------------------------------
  function display() {
    //-----------------------------------------------------------------
    
    let body = null;
    if(is_loading) {
      body = (
        <Paper sx={_styles.spinner_box}>
          <CircularProgress sx={_styles.spinner} size='6rem' />
          <Typography variant='button' sx={_styles.spinner_caption} display='block'>
            Evaluating
          </Typography>
        </Paper>
      )
    } else {
      body = (
        <Box {...CONTAINER_CONTENT}>
          <Box {...CONTAINER_FILE_INFO}>
            <FileInfo />
          </Box>
          <Box {...CONTAINER_RULE_INFO}>
            <MissingDocs   />
            <DocumentView  />
            <Exceptions file_id={file_id} items={rules} type={'DATA'} />
          </Box>
        </Box>
      )
    }

    return (
      <>
        <HeaderFileView />
        <Box {...CONTAINER}>
          { body }
        </Box>
      </>
    );
  }

  //#endregion

  return display();
}
